import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, AlertCircle, Eye, Pencil, Trash2, Wallet, MinusCircle, CircleDollarSign, User,
} from "lucide-react";

const MODE_LABEL = {
  cash: { label: "Cash", chip: "bg-[#E8F5E9] text-[#2E7D32]" },
  online: { label: "Online", chip: "bg-[#E3F2FD] text-[#1565C0]" },
  salary_deduction: { label: "Salary deduction", chip: "bg-[#FFF3E0] text-[#E65100]" },
};

const EMPTY_FORM = { employee_id: "", name: "", total_amount: "", notes: "" };

export default function EmployeeOtherDeductionsPage() {
  const { selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";

  const [employees, setEmployees] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);

  const load = useCallback(async () => {
    if (!selectedBranch) { setEmployees([]); setDeductions([]); return; }
    setLoading(true);
    try {
      const [emp, ded] = await Promise.all([
        api.get("/employees", { params: { branch_id: selectedBranch.id } }),
        api.get("/other-deductions", { params: { branch_id: selectedBranch.id } }),
      ]);
      setEmployees(emp.data || []);
      setDeductions(ded.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [selectedBranch]);

  useEffect(() => { load(); }, [load]);

  const employeeById = useMemo(() => {
    const m = {}; employees.forEach((e) => { m[e.id] = e; }); return m;
  }, [employees]);

  const totals = useMemo(() => deductions.reduce((acc, d) => ({
    total: acc.total + d.total_amount,
    paid: acc.paid + d.paid_amount,
    remaining: acc.remaining + d.remaining_amount,
  }), { total: 0, paid: 0, remaining: 0 }), [deductions]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setOpenForm(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ employee_id: d.employee_id, name: d.name, total_amount: String(d.total_amount), notes: d.notes || "" });
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedBranch || !form.employee_id) return toast.error("Pick an employee");
    if (Number(form.total_amount) <= 0) return toast.error("Amount must be greater than zero");
    setBusy(true);
    try {
      const payload = { ...form, total_amount: Number(form.total_amount) };
      if (editing) {
        const { data } = await api.patch(`/other-deductions/${editing.id}`, payload);
        setDeductions((prev) => prev.map((x) => (x.id === editing.id ? data : x)));
        toast.success("Updated");
      } else {
        const { data } = await api.post("/other-deductions", { ...payload, branch_id: selectedBranch.id });
        setDeductions((prev) => [data, ...prev]);
        toast.success("Created");
      }
      setOpenForm(false); setEditing(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const removeDeduction = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/other-deductions/${deleteTarget.id}`);
      setDeductions((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reloadOne = async (id) => {
    if (!selectedBranch) return;
    const { data } = await api.get("/other-deductions", { params: { branch_id: selectedBranch.id } });
    const updated = (data || []).find((x) => x.id === id);
    if (updated) setDeductions((prev) => prev.map((x) => (x.id === id ? updated : x)));
    return updated;
  };

  return (
    <>
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Employee Other Deductions</h1>
          <p className="text-brand-inkMuted mt-0.5 text-sm">
            Track one-time deductions per employee. Only payments marked as <span className="font-semibold">Salary deduction</span> reduce the pay slip.
          </p>
        </div>
        <Button onClick={openAdd} disabled={!selectedBranch} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm h-11 px-5" data-testid="add-deduction-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Deduction
        </Button>
      </div>

      {!selectedBranch ? <NoBranchCard /> : (
        <>
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4">
            <div className="grid sm:grid-cols-4 gap-3 items-center">
              <div>
                <Label className="text-xs font-heading text-brand-inkMuted">Branch</Label>
                <select value={selectedBranch.id} onChange={(e) => setSelectedBranchId(e.target.value)}
                  className="mt-1 w-full h-10 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
                  data-testid="branch-select">
                  {branchesForSelectedSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <StatTile icon={<CircleDollarSign className="w-4 h-4" />} label="TOTAL OWED" value={`${currency} ${totals.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="blue" testId="stat-total" />
              <StatTile icon={<Wallet className="w-4 h-4" />} label="TOTAL PAID" value={`${currency} ${totals.paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="green" testId="stat-paid" />
              <StatTile icon={<MinusCircle className="w-4 h-4" />} label="REMAINING" value={`${currency} ${totals.remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="red" testId="stat-remaining" />
            </div>
          </section>

          {loading ? <p className="text-brand-inkMuted">Loading...</p>
            : deductions.length === 0 ? <EmptyState onAdd={openAdd} />
              : (
                <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="deductions-table">
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[24%]" /><col className="w-[24%]" /><col className="w-[14%]" />
                      <col className="w-[14%]" /><col className="w-[14%]" /><col className="w-[10%]" />
                    </colgroup>
                    <thead className="bg-[#F1F5FF] text-brand-inkMuted">
                      <tr>
                        <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider text-[11px]">EMPLOYEE</th>
                        <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider text-[11px]">DEDUCTION</th>
                        <th className="text-right px-2 py-3 font-heading font-semibold tracking-wider text-[11px]">AMOUNT</th>
                        <th className="text-right px-2 py-3 font-heading font-semibold tracking-wider text-[11px]">PAID</th>
                        <th className="text-right px-2 py-3 font-heading font-semibold tracking-wider text-[11px]">REMAINING</th>
                        <th className="text-center px-2 py-3 font-heading font-semibold tracking-wider text-[11px]">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deductions.map((d) => (
                        <DeductionRow key={d.id} d={d} employee={employeeById[d.employee_id]} currency={currency}
                          onPay={() => setPayTarget(d)} onView={() => setViewTarget(d)} onEdit={() => openEdit(d)} onDelete={() => setDeleteTarget(d)} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
        </>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={openForm} onOpenChange={(o) => { setOpenForm(o); if (!o) setEditing(null); }}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-md" data-testid="deduction-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit deduction" : "Add deduction"}</DialogTitle>
            <DialogDescription>Record a deduction owed by an employee. Track payments individually.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="deduction-form">
            <FormField label="Employee" required>
              <select required disabled={!!editing} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
                data-testid="form-employee">
                <option value="">Select employee...</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.position}</option>)}
              </select>
            </FormField>
            <FormField label="Name / Description" required>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Cash advance Jan 2026"
                className="rounded-2xl h-11 border-brand-purple/20" data-testid="form-name" />
            </FormField>
            <FormField label="Amount" required>
              <Input required type="number" min="0.01" step="0.01" value={form.total_amount}
                onChange={(e) => setForm({ ...form, total_amount: e.target.value })} placeholder="5000.00"
                className="rounded-2xl h-11 border-brand-purple/20" data-testid="form-total" />
            </FormField>
            <FormField label="Notes">
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional"
                className="rounded-2xl h-11 border-brand-purple/20" data-testid="form-notes" />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenForm(false)} className="rounded-full border-brand-purple/30">Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="form-submit">
                {busy ? "Saving..." : editing ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PaymentDialog target={payTarget} onClose={() => setPayTarget(null)} currency={currency}
        onSaved={async () => { if (payTarget) await reloadOne(payTarget.id); }} />

      <ViewDialog target={viewTarget} employee={viewTarget ? employeeById[viewTarget.employee_id] : null}
        onClose={() => setViewTarget(null)} currency={currency}
        onChange={async () => { if (viewTarget) await reloadOne(viewTarget.id); }} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this deduction?</AlertDialogTitle>
            <AlertDialogDescription>This removes the deduction and its full payment history. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeDeduction} className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="confirm-delete-deduction">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DeductionRow({ d, employee, currency, onPay, onView, onEdit, onDelete }) {
  const progress = d.total_amount > 0 ? Math.min(100, Math.round((d.paid_amount / d.total_amount) * 100)) : 0;
  return (
    <tr className="border-t border-brand-purple/5 hover:bg-[#FAFBFE]" data-testid={`deduction-row-${d.id}`}>
      <td className="px-3 py-3">
        <p className="font-heading font-semibold text-foreground text-sm uppercase truncate">{employee?.name || "—"}</p>
        <p className="text-[11px] text-brand-inkMuted truncate">{employee?.position || ""}</p>
      </td>
      <td className="px-3 py-3">
        <p className="text-sm text-foreground truncate">{d.name}</p>
        {d.notes && <p className="text-[11px] text-brand-inkMuted truncate">{d.notes}</p>}
        <div className="mt-1.5 h-1.5 rounded-full bg-brand-purpleLight overflow-hidden">
          <div className={`h-full ${d.is_fully_paid ? "bg-[#2E7D32]" : "bg-brand-purple"}`} style={{ width: `${progress}%` }} />
        </div>
      </td>
      <td className="px-2 py-3 text-right font-body text-sm">{currency} {d.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td className="px-2 py-3 text-right font-body text-sm text-[#2E7D32]">{currency} {d.paid_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td className="px-2 py-3 text-right font-body text-sm text-[#C62828]">{currency} {d.remaining_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td className="px-2 py-3">
        <div className="flex items-center justify-center gap-1">
          <IconBtn onClick={onPay} disabled={d.is_fully_paid} tone="purple" label="Add payment" testId={`pay-${d.id}`}><Wallet className="w-4 h-4" /></IconBtn>
          <IconBtn onClick={onView} tone="muted" label="View" testId={`view-${d.id}`}><Eye className="w-4 h-4" /></IconBtn>
          <IconBtn onClick={onEdit} tone="muted" label="Edit" testId={`edit-${d.id}`}><Pencil className="w-4 h-4" /></IconBtn>
          <IconBtn onClick={onDelete} tone="red" label="Delete" testId={`delete-${d.id}`}><Trash2 className="w-4 h-4" /></IconBtn>
        </div>
      </td>
    </tr>
  );
}

function IconBtn({ children, onClick, label, tone, disabled, testId }) {
  const tones = {
    purple: "text-brand-purple hover:bg-brand-purpleLight",
    muted: "text-brand-inkMuted hover:bg-brand-purpleLight hover:text-foreground",
    red: "text-[#C62828] hover:bg-[#FFEBEE]",
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={testId}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tones[tone] || tones.muted}`}>
      {children}
    </button>
  );
}

function PaymentDialog({ target, onClose, currency, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [onlineDetails, setOnlineDetails] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setDate(new Date().toISOString().slice(0, 10));
      setAmount("");
      setMode("cash");
      setOnlineDetails("");
      setNote("");
    }
  }, [target]);

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return;
    if (Number(amount) <= 0) return toast.error("Amount must be greater than zero");
    if (mode === "online" && !onlineDetails.trim()) return toast.error("Please add online payment details");
    setBusy(true);
    try {
      await api.post(`/other-deductions/${target.id}/payments`, {
        date, amount: Number(amount), mode,
        online_details: mode === "online" ? onlineDetails : "",
        note,
      });
      toast.success("Payment recorded");
      await onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl border-brand-purple/20 max-w-md" data-testid="payment-dialog">
        {target && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Add payment</DialogTitle>
              <DialogDescription>
                {target.name} — remaining {currency} {target.remaining_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <FormField label="Date" required>
                <Input required type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-2xl h-11 border-brand-purple/20" data-testid="payment-date" />
              </FormField>
              <FormField label="Amount" required>
                <Input required type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                  className="rounded-2xl h-11 border-brand-purple/20" data-testid="payment-amount" />
              </FormField>
              <FormField label="Mode of payment" required>
                <select required value={mode} onChange={(e) => setMode(e.target.value)}
                  className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
                  data-testid="payment-mode">
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                  <option value="salary_deduction">Salary deduction</option>
                </select>
              </FormField>
              {mode === "online" && (
                <FormField label="Online payment details" required>
                  <Input required value={onlineDetails} onChange={(e) => setOnlineDetails(e.target.value)}
                    placeholder="e.g. GCash ref #1234567 / BPI transfer / Bank reference"
                    className="rounded-2xl h-11 border-brand-purple/20" data-testid="payment-online-details" />
                </FormField>
              )}
              {mode === "salary_deduction" && (
                <p className="text-[11px] text-brand-inkMuted bg-[#FFF3E0] border border-[#E65100]/20 rounded-xl px-3 py-2">
                  This payment will reduce the pay slip on the period that contains <span className="font-semibold">{date}</span>.
                </p>
              )}
              <FormField label="Note">
                <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-2xl h-11 border-brand-purple/20" data-testid="payment-note" />
              </FormField>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose} className="rounded-full border-brand-purple/30">Cancel</Button>
                <Button type="submit" disabled={busy} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="payment-submit">
                  {busy ? "Saving..." : "Save payment"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ViewDialog({ target, employee, onClose, currency, onChange }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/other-deductions/${target.id}/payments`);
        setPayments(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [target]);

  const removePayment = async (p) => {
    try {
      await api.delete(`/other-deductions/payments/${p.id}`);
      setPayments((prev) => prev.filter((x) => x.id !== p.id));
      await onChange();
      toast.success("Payment removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!target) return null;
  const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const remaining = Math.max(0, target.total_amount - totalPaid);

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl border-0 max-w-2xl p-0 overflow-hidden bg-white" data-testid="view-dialog">
        <div className="relative h-24 bg-gradient-to-br from-[#2563EB] via-[#3B82F6] to-[#60A5FA]">
          <div className="absolute -bottom-10 left-6 w-20 h-20 rounded-3xl ring-4 ring-white shadow-lg bg-brand-purpleLight overflow-hidden flex items-center justify-center">
            {employee?.picture ? <img src={employee.picture} alt={employee.name} className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-brand-purple" />}
          </div>
        </div>
        <div className="pt-14 pb-6 px-6">
          <DialogHeader className="text-left space-y-1 mb-4">
            <DialogTitle className="font-heading text-xl sm:text-2xl">{target.name}</DialogTitle>
            <DialogDescription className="uppercase tracking-wider text-xs">{employee?.name || "—"} · {employee?.position || ""}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <KPI label="TOTAL AMOUNT" value={`${currency} ${target.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="blue" />
            <KPI label="TOTAL PAID" value={`${currency} ${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="green" />
            <KPI label="REMAINING" value={`${currency} ${remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="red" />
          </div>

          {target.notes && (
            <div className="rounded-xl bg-[#F8FAFF] border border-brand-purple/10 px-3 py-2 text-sm text-brand-inkMuted mb-4">
              <span className="font-heading font-semibold text-foreground">Notes:</span> {target.notes}
            </div>
          )}

          <p className="font-heading text-[10px] uppercase tracking-[0.18em] text-brand-purple mb-2">Payment history</p>
          {loading ? <p className="text-sm text-brand-inkMuted">Loading...</p>
            : payments.length === 0 ? <p className="text-sm text-brand-inkMuted">No payments recorded yet.</p>
              : (
                <ul className="space-y-1.5 max-h-72 overflow-y-auto">
                  {payments.map((p) => (
                    <li key={p.id} className="flex items-center justify-between rounded-2xl border border-brand-purple/10 px-3 py-2" data-testid={`history-row-${p.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-semibold text-foreground text-sm">{currency} {Number(p.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${MODE_LABEL[p.mode]?.chip}`}>{MODE_LABEL[p.mode]?.label}</span>
                          <span className="text-[11px] text-brand-inkMuted">{p.date}</span>
                        </div>
                        {p.mode === "online" && p.online_details && <p className="text-[11px] text-brand-inkMuted truncate">Ref: {p.online_details}</p>}
                        {p.note && <p className="text-[11px] text-brand-inkMuted truncate">{p.note}</p>}
                      </div>
                      <button onClick={() => removePayment(p)} className="text-[#C62828] hover:text-[#B71C1C]" data-testid={`delete-payment-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

          <DialogFooter className="mt-5">
            <Button onClick={onClose} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white px-6 h-10">Close</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, tone }) {
  const tones = {
    blue: "bg-[#E3F2FD] text-[#1565C0]",
    green: "bg-[#E8F5E9] text-[#2E7D32]",
    red: "bg-[#FFEBEE] text-[#C62828]",
  };
  return (
    <div className="rounded-2xl border border-brand-purple/10 bg-white p-3 text-center">
      <span className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-heading font-semibold tracking-[0.15em] ${tones[tone] || tones.blue}`}>{label}</span>
      <p className="font-heading text-base font-semibold text-foreground mt-1.5 truncate">{value}</p>
    </div>
  );
}

function StatTile({ icon, label, value, tone, testId }) {
  const toneStyles = { blue: "bg-[#E3F2FD] text-[#1565C0]", green: "bg-[#E8F5E9] text-[#2E7D32]", red: "bg-[#FFEBEE] text-[#C62828]" }[tone] || "bg-brand-purpleLight text-foreground";
  return (
    <div className="rounded-2xl border border-brand-purple/10 bg-white p-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-brand-inkMuted">
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-lg ${toneStyles}`}>{icon}</span>{label}
      </div>
      <p className="font-heading text-base sm:text-lg font-semibold text-foreground mt-1.5">{value}</p>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <Label className="font-heading text-sm">{label} {required && <span className="text-[#C62828]">*</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-purple/10 p-10 text-center" data-testid="no-deductions">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3"><MinusCircle className="w-7 h-7 text-brand-purple" /></div>
      <p className="font-heading text-lg font-semibold text-foreground">No deductions yet</p>
      <p className="text-brand-inkMuted text-sm mt-1 mb-4">Add a deduction for an employee and record payments as they come in.</p>
      <Button onClick={onAdd} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white"><Plus className="w-4 h-4 mr-1.5" /> Add deduction</Button>
    </div>
  );
}

function NoBranchCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3"><AlertCircle className="w-7 h-7 text-[#E65100]" /></div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
      <p className="text-brand-inkMuted mt-1 text-sm">Other Deductions are scoped per branch. Use the dropdown in the header.</p>
    </div>
  );
}
